/**
 * Brera · Ficha de producto (sections/product.liquid)
 * - Galería: deslizado, puntos, flechas, miniaturas, salto a la foto de la variante y zoom en ventana.
 * - Variante ('variant:change' de assets/variant-picker.js): precio, botón, barra fija, inventario bajo y foto.
 * - Cantidad (+ / −), agregar al carrito sin recargar (aviso con "Ver carrito"; emite 'cart:updated').
 * - Ventanas (guía de tallas), copiar código o enlace, barra fija cuando el botón sale de la pantalla.
 */
(() => {
  if (window.BreraProduct) return;

  const B = window.Brera || {};
  const root = (B.routes && B.routes.root) || '/';
  const money = (cents) => (window.BreraQuickAdd ? window.BreraQuickAdd.formatMoney(cents) : '$' + (cents / 100).toFixed(0));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ---------- Galería ---------- */
  const initGallery = (root, onIndex) => {
    const gallery = root.querySelector('[data-gallery]');
    if (!gallery) return { goToMedia: () => {} };
    const track = gallery.querySelector('[data-gallery-track]');
    const items = [...track.children];
    const dots = [...gallery.querySelectorAll('.pg__dot')];
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const isSlider = () => track.scrollWidth > track.clientWidth + 2;

    const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const mark = (i) => {
      dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
      thumbs.forEach((t, j) => t.classList.toggle('is-active', i === j));
      const active = thumbs[i];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (onIndex) onIndex(i);
    };
    const goTo = (i, smooth = true) => {
      const n = items.length;
      const idx = ((i % n) + n) % n;
      if (isSlider()) {
        track.scrollTo({ left: track.clientWidth * idx, behavior: smooth ? 'smooth' : 'auto' });
      } else if (items[idx]) {
        items[idx].scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
      }
      mark(idx);
    };

    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { if (isSlider()) mark(current()); });
    }, { passive: true });
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(current() - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(current() + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.thumb))));

    const goToMedia = (mediaId) => {
      if (!mediaId) return;
      const idx = items.findIndex((el) => el.dataset.mediaId === String(mediaId));
      if (idx >= 0 && isSlider()) goTo(idx);
      else if (idx >= 0) mark(idx);
    };

    const start = gallery.dataset.start;
    if (start) {
      const idx = items.findIndex((el) => el.dataset.mediaId === start);
      if (idx > 0 && isSlider()) requestAnimationFrame(() => goTo(idx, false));
    }

    /* Zoom */
    const zoom = root.querySelector('[data-pd-zoom]');
    if (zoom) {
      const zImg = zoom.querySelector('[data-zoom-image]');
      const images = items.map((el) => el.querySelector('.pg__image')).filter(Boolean);
      let zIndex = 0;
      const bestSrc = (img) => {
        const set = (img.getAttribute('srcset') || '').split(',').map((s) => s.trim().split(' ')[0]).filter(Boolean);
        return set[set.length - 1] || img.currentSrc || img.src;
      };
      const show = (i) => {
        zIndex = (i + images.length) % images.length;
        zImg.src = bestSrc(images[zIndex]);
        zImg.alt = images[zIndex].alt || '';
      };
      gallery.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-zoom]');
        if (!btn) return;
        const img = btn.querySelector('.pg__image');
        show(images.indexOf(img));
        zoom.showModal();
      });
      zoom.querySelector('[data-zoom-prev]').addEventListener('click', () => show(zIndex - 1));
      zoom.querySelector('[data-zoom-next]').addEventListener('click', () => show(zIndex + 1));
      zoom.addEventListener('click', (e) => { if (e.target === zoom || e.target === zImg) zoom.close(); });
      zoom.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') show(zIndex - 1);
        if (e.key === 'ArrowRight') show(zIndex + 1);
      });
    }

    /* Videos subidos: se reproducen solo mientras se ven (al menos la mitad en pantalla) y se pausan al salir.
       Así no gastan datos ni batería los que están fuera de la vista. */
    const videos = [...gallery.querySelectorAll('.pg__video')];
    if (videos.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.5 });
      videos.forEach((v) => {
        v.muted = true;
        io.observe(v);
      });
    }

    return { goToMedia };
  };

  /* ---------- Ficha ---------- */
  const initProduct = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const variants = JSON.parse(root.querySelector('[data-pd-variants]')?.textContent || '[]');
    const t = JSON.parse(root.querySelector('[data-pd-strings]')?.textContent || '{}');
    const form = root.querySelector('[data-pd-form]');
    const addBtn = root.querySelector('[data-pd-add]');
    const msg = root.querySelector('[data-pd-msg]');
    const sticky = root.querySelector('[data-pd-sticky]');
    const stickyBtn = root.querySelector('[data-pd-sticky-add]');
    const buyBtn = root.querySelector('[data-pd-buy]');
    const notify = root.querySelector('[data-pd-notify]');
    const handleize = (str) => String(str).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    /* Recoger en tienda: muestra lo de la variante elegida */
    const showPickup = (id) => {
      root.querySelectorAll('[data-pd-pickup]').forEach((box) => {
        const tpl = box.querySelector(`[data-pd-pickup-for="${id}"]`);
        const target = box.querySelector('[data-pd-pickup-target]');
        if (target) target.innerHTML = tpl ? tpl.innerHTML.trim() : '';
      });
    };
    const firstId = form && form.querySelector('[name="id"]') ? form.querySelector('[name="id"]').value : null;
    if (firstId) showPickup(firstId);

    /* Avísame: si la página vuelve del registro, se muestra el mensaje */
    if (notify && window.location.hash === `#${notify.dataset.formId}`) {
      notify.hidden = false;
      notify.scrollIntoView({ block: 'center' });
    }

    const gallery = initGallery(root);

    /* Variante elegida */
    root.addEventListener('variant:change', (e) => {
      const picked = e.detail && e.detail.variant;
      const v = picked ? variants.find((x) => x.id === picked.id) : null;
      const priceBox = root.querySelector('[data-pd-price]');
      if (!v) {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = addBtn.dataset.soldOut; }
        if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = addBtn ? addBtn.dataset.soldOut : ''; }
        if (buyBtn) buyBtn.disabled = true;
        if (notify) notify.hidden = false;
        return;
      }
      if (priceBox) {
        const sale = v.compare > v.price && t.showComparePrice !== false;
        priceBox.innerHTML = `<div class="price price--large${v.compare > v.price ? ' price--on-sale' : ''}${v.available ? '' : ' price--sold-out'}" data-price><span class="price__current">${money(v.price)}</span>${sale ? `<s class="price__compare">${money(v.compare)}</s>` : ''}</div>`;
      }
      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available
          ? addBtn.dataset.label + (addBtn.dataset.showPrice === 'true' ? ' · ' + money(v.price) : '')
          : addBtn.dataset.soldOut;
      }
      if (buyBtn) {
        buyBtn.disabled = !v.available;
        buyBtn.textContent = buyBtn.dataset.label + (buyBtn.dataset.showPrice === 'true' ? ' · ' + money(v.price) : '');
      }
      if (stickyBtn) {
        stickyBtn.disabled = !v.available;
        stickyBtn.textContent = v.available ? `${t.stickyLabel} · ${money(v.price)}` : (addBtn ? addBtn.dataset.soldOut : '');
      }
      const sv = root.querySelector('[data-pd-sticky-variant]');
      if (sv) sv.textContent = v.title === 'Default Title' ? '' : v.title;
      const stock = root.querySelector('[data-pd-stock]');
      if (stock) {
        const low = t.lowStock && v.available && v.stock !== null && v.stock > 0 && v.stock <= t.threshold;
        stock.innerHTML = low ? `<p class="low-stock low-stock--product" style="--low-stock: ${esc(t.lowStockColor)};"><span class="low-stock__dot" aria-hidden="true"></span>${esc(t.lowStock.replace('[n]', v.stock))}</p>` : '';
      }
      if (v.media) gallery.goToMedia(v.media);
      /* Insignias de la variante */
      const badges = root.querySelector('[data-pd-badges]');
      const badgeTpl = root.querySelector(`[data-pd-badges-for="${v.id}"]`);
      if (badges && badgeTpl) badges.innerHTML = badgeTpl.innerHTML;
      showPickup(v.id);
      /* Avísame: solo con la talla agotada; la etiqueta lleva producto y talla */
      if (notify) {
        notify.hidden = v.available;
        const tags = notify.querySelector('[data-pd-notify-tags]');
        if (tags) {
          const tag = v.title && v.title !== 'Default Title' ? `${tags.dataset.base}-${handleize(v.title)}` : tags.dataset.base;
          tags.value = `avisame, ${tag}`;
        }
      }
    });

    /* Cantidad */
    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-qty]');
      if (!b) return;
      const input = b.parentElement.querySelector('input');
      input.value = Math.max(1, (Number(input.value) || 1) + Number(b.dataset.qty));
    });

    /* Agregar al carrito sin recargar. Pide en el mismo pedido las secciones del carrito ([data-cart-root]) para que
       assets/cart.js abra el carrito lateral sin esperar otro pedido. */
    const cartSectionsBody = () => {
      const ids = [...new Set([...document.querySelectorAll('[data-cart-root][data-section-id]')].map((el) => el.dataset.sectionId))];
      return ids.length ? { sections: ids, sections_url: window.location.pathname } : {};
    };
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const buyNow = !!(e.submitter && e.submitter.hasAttribute('data-pd-buy'));
        const id = form.querySelector('[name="id"]').value;
        const qty = Number(form.querySelector('[name="quantity"]')?.value) || 1;
        (buyNow ? [buyBtn] : [addBtn, stickyBtn]).forEach((b) => b && b.classList.add('is-loading'));
        msg.classList.remove('is-error');
        try {
          const res = await fetch(root.dataset.cartAddUrl || (window.Brera?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(Object.assign({ items: [{ id: Number(id), quantity: qty }] }, buyNow ? {} : cartSectionsBody())),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || t.error);
          if (buyNow) {
            /* Comprar ahora: directo al pago */
            window.location.href = (window.Brera?.routes?.root || '/') + 'checkout';
            return;
          }
          /* Con carrito lateral el aviso sobra: se abre el carrito (assets/cart.js) */
          if (!(window.Brera?.cart?.drawer && window.Brera.cart.openOnAdd)) {
            msg.innerHTML = `${esc(t.added)} · <a href="${root.dataset.cartUrl || '/cart'}">${esc(t.viewCart)}</a>`;
          } else {
            msg.textContent = '';
          }
          /* El carrito lateral se abre ya con lo que trajo el mismo pedido; el contador se actualiza después */
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections || null, source: 'product' } }));
          fetch((window.Brera?.routes?.root || '/') + 'cart.js').then((r) => r.json()).then((cart) => {
            document.querySelectorAll('[data-cart-count]').forEach((el) => {
              el.textContent = cart.item_count;
              el.classList.toggle('is-empty', cart.item_count === 0);
            });
          }).catch(() => {});
        } catch (err) {
          msg.textContent = err.message || t.error;
          msg.classList.add('is-error');
        }
        [addBtn, stickyBtn, buyBtn].forEach((b) => b && b.classList.remove('is-loading'));
      });
    }

    /* Ventanas */
    root.addEventListener('click', (e) => {
      const open = e.target.closest('[data-dialog-open]');
      if (open) {
        const d = document.getElementById(open.dataset.dialogOpen);
        if (d) d.showModal();
        return;
      }
      const close = e.target.closest('[data-dialog-close]');
      if (close) close.closest('dialog')?.close();
    });
    root.querySelectorAll('.pd-dialog').forEach((d) => {
      d.addEventListener('click', (e) => { if (e.target === d) d.close(); });
    });

    /* Copiar código o enlace */
    root.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-copy]');
      if (!b) return;
      try { await navigator.clipboard.writeText(b.dataset.copy); } catch (err) { /* sin portapapeles */ }
      const label = b.querySelector('[data-copy-label]');
      if (!label) return;
      const before = label.textContent;
      label.textContent = b.dataset.copied || before;
      setTimeout(() => { label.textContent = before; }, 1800);
    });

    /* Barra fija: aparece cuando el botón principal sale de la pantalla */
    if (sticky && addBtn && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', passed);
        /* Sube WhatsApp y "volver arriba" mientras la barra está a la vista */
        const shown = passed && getComputedStyle(sticky).display !== 'none';
        document.body.classList.toggle('pd-sticky-on', shown);
        if (shown) document.body.style.setProperty('--pd-sticky-h', `${sticky.offsetHeight + 12}px`);
        sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
        if (stickyBtn) stickyBtn.tabIndex = passed ? 0 : -1;
      });
      io.observe(addBtn);
    }

    /* Columnas fijas (computador): si la columna es más alta que la pantalla,
       baja con la página hasta que se ve su final y ahí se queda quieta. */
    const cols = root.querySelectorAll('[data-pd-sticky-col]');
    if (cols.length) {
      const place = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const base = header + 24;
        cols.forEach((col) => {
          const top = Math.min(base, window.innerHeight - col.offsetHeight - 24);
          col.style.setProperty('--pd-col-top', `${Math.round(top)}px`);
        });
      };
      place();
      window.addEventListener('resize', place, { passive: true });
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(place);
        cols.forEach((c) => ro.observe(c));
      }
    }
  };

  const init = (scope) => scope.querySelectorAll('[data-product]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* Comprar por WhatsApp: arma el mensaje con la talla, la cantidad y el precio elegidos */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pd-wa-buy]');
    if (!btn) return;
    const root = btn.closest('.pd[data-product]') || btn.closest('[data-section-id][data-product]');
    if (!root) return;
    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-pd-variants]').textContent); } catch (err) {}
    const form = root.querySelector('[data-pd-form]');
    const id = Number(form && form.querySelector('[name="id"]') ? form.querySelector('[name="id"]').value : 0);
    const v = variants.find((x) => x.id === id) || variants[0];
    const qtyInput = form && form.querySelector('[name="quantity"]');
    const qty = Math.max(1, Number(qtyInput ? qtyInput.value : 1) || 1);
    const size = v && variants.length > 1 ? v.title : '';
    const price = v ? money(v.price * qty) : '';
    const text = (btn.dataset.message || '')
      .replace(/\[producto\]/g, btn.dataset.productTitle || '')
      .replace(/\[talla\]/g, size)
      .replace(/\[cantidad\]/g, String(qty))
      .replace(/\[precio\]/g, price)
      .replace(/\[enlace\]/g, btn.dataset.url || '')
      .replace(/ · Talla: (?= ·|$)/, '');
    btn.href = 'https://wa.me/' + btn.dataset.number + '?text=' + encodeURIComponent(text.trim());
  });

  window.BreraProduct = { init };
})();
